import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { OrganizationsState } from '../OrganizationsState';
import { IOrganization } from '../IOrganization';
import { Logger } from '../../support/Logger';
import { UploadsClient } from '../../media/UploadsClient';
import { DemoClientPhotos } from '../insights/DemoClientPhotos';

interface IDemoDataRefreshProgress {
    isRunning: boolean;
    done: number;
    total: number;
    failed: number;
    /** Clients past the last storyline, whom the api leaves with no demo data. */
    withoutStoryline: number;
}

interface IDemoDataRefreshResult {
    storylineKey: string | null;
}

// Each client is several dozen writes, so a few at once gets through a caseload quickly without a
// burst big enough to throttle.
const CONCURRENT_CLIENTS = 4;

/**
 * Re-seeds every client of a demo organization with its invented record, dated back from now, and
 * deals each a stock portrait, uploaded and handed to the api with the refresh, so the caseload
 * shows faces rather than initials as the admin-only demo caseload does. The api takes one client
 * per request, so a caseload stays inside the request time limit and this can say how far it has got.
 */
export class DemoDataRefreshState extends State<IDemoDataRefreshProgress> {
    static inject = () => [APIClient, OrganizationsState, Logger, UploadsClient, DemoClientPhotos];
    constructor(
        private apiClient: APIClient,
        private orgState: OrganizationsState,
        private logger: Logger,
        private uploads: UploadsClient,
        private photos: DemoClientPhotos
    ) {
        super({ isRunning: false, done: 0, total: 0, failed: 0, withoutStoryline: 0 });
    }

    refresh = async (org: IOrganization): Promise<void> => {
        const link = org.links.demoData;
        if (!link || this.state.isRunning) {
            return;
        }
        // Claimed before the caseload loads, so a second press during the load cannot start a second run.
        this.setState({ isRunning: true, done: 0, total: 0, failed: 0, withoutStoryline: 0 });

        try {
            await this.orgState.loadOrganizationCareMaps(org.orgId);
        } catch (error) {
            this.setState({ isRunning: false });
            throw error;
        }
        const careMaps = this.orgState.getOrganizationCareMaps(org.orgId);
        const dealt = this.photos.deal({ count: careMaps.length });
        const pending = careMaps.map((careMap, index) => ({ careMap, photoFile: dealt[index] }));
        this.setState({ total: pending.length });

        const work = async () => {
            for (let next = pending.shift(); next; next = pending.shift()) {
                const { careMap, photoFile } = next;
                try {
                    const photo = photoFile ? await this.uploadPhoto({ photoFile, careMapId: careMap.careMapId }) : undefined;
                    const result: IDemoDataRefreshResult = await this.apiClient.post(link, { photo }, undefined, { userId: careMap.userId, careMapId: careMap.careMapId });
                    if (result.storylineKey === null) {
                        this.setState({ withoutStoryline: this.state.withoutStoryline + 1 });
                    }
                } catch (error) {
                    // One client failing should not stop the rest.
                    this.logger.error(error, { context: 'demo data refresh failed for one client', careMapId: careMap.careMapId });
                    this.setState({ failed: this.state.failed + 1 });
                }
                this.setState({ done: this.state.done + 1 });
            }
        };

        try {
            await Promise.all(Array.from({ length: CONCURRENT_CLIENTS }, work));
        } finally {
            this.setState({ isRunning: false });
        }
    };

    /**
     * The client's dealt portrait, uploaded. Best effort: a photo that cannot be read or uploaded
     * leaves the client with their record refreshed and their face as it was, rather than unrefreshed.
     */
    private uploadPhoto = async (params: { photoFile: string; careMapId: string }): Promise<string | undefined> => {
        const { photoFile, careMapId } = params;
        try {
            return (await this.uploads.upload(await this.bundledFile(photoFile))).url;
        } catch (error) {
            this.logger.error(error, { context: 'demo data refresh could not upload a photo for one client', careMapId });
            return undefined;
        }
    };

    /** A portrait the app ships with, as a file the uploads route takes. */
    private bundledFile = async (path: string): Promise<File> => {
        const response = await window.fetch(path);
        if (!response.ok) {
            throw new Error(`Could not read the bundled photo ${path}: ${response.status}`);
        }
        const name = path.split('/').pop() || 'photo.jpg';
        return new File([await response.blob()], name, { type: 'image/jpeg' });
    };
}
